/**
 * Content deep links — a document, a board (and a task in it) — opened from
 * outside the app: the browser, chat, mail, Finder, another app.
 *
 *   apiboo://documents/<scope>/<docId>                    (documentsActions.documentLink)
 *   apiboo://boards/<scope>/<boardId>[/tasks/<taskId>]    (utils/boardLinks.taskLink)
 *
 * Rust (src-tauri/src/content_link.rs) reads the URL the OS hands over —
 * however it came in: a running app, a second launch, or the app launched BY
 * the link — and keeps it in an inbox. It pokes this page with
 * `content-link-pending`; the page drains the inbox (`deep_link_take_pending`)
 * on the poke AND once when it starts listening, so a link that arrived before
 * React mounted is not lost.
 *
 * Links are then QUEUED HERE until the app is ready — signed in, past the
 * sign-in gate (desktopGate 'app'). A link opened while the sign-in page is up
 * opens right after sign-in, never before, and is never dropped. Right after
 * sign-in Rust may still be swapping in the account's database, so a link
 * that waited is looked up a few times before "not found" is said.
 *
 * Owner, 26.10.2026: "apiboo://documents/personal/<id> — deep links are not
 * working -> does not open that document in a new tab". Rust had no reader
 * for `documents/` at all, and nothing here listened for one.
 */
import { useEffect, useRef } from 'react';
import { isTauri } from '../utils/tauri';
import { parseDocumentLink } from '../mcp/reveal';

export const PENDING_EVENT = 'content-link-pending';
export const TAKE_COMMAND = 'deep_link_take_pending';

/** A link that waited for sign-in is tried this many times, this far apart. */
export const SETTLE_ATTEMPTS = 4;
export const SETTLE_DELAY_MS = 750;

let queue = []; // [{ link, early }]
let ready = false;
let draining = null;

const sameLink = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * What Rust sent → the link this page opens, or null. Checked again here (the
 * page trusts no payload): a document goes through `parseDocumentLink`, the
 * same reader the sidebar's search box uses; a board needs plain ids.
 */
export function normalizeContentLink(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.kind === 'document') {
    const scope = String(raw.scope || '');
    const docId = String(raw.docId || '');
    const uri = raw.scopeId
      ? `apiboo://documents/personal/${encodeURIComponent(String(raw.scopeId))}/${encodeURIComponent(docId)}`
      : `apiboo://documents/${encodeURIComponent(scope)}/${encodeURIComponent(docId)}`;
    if (raw.scopeId && scope !== 'personal') return null;
    const link = parseDocumentLink(uri);
    return link ? { kind: 'document', ...link } : null;
  }
  if (raw.kind === 'board') {
    const ok = (s) => typeof s === 'string' && /^[A-Za-z0-9._-]{1,128}$/.test(s);
    if (!ok(raw.boardId) || (raw.taskId != null && !ok(raw.taskId))) return null;
    return { kind: 'board', boardId: raw.boardId, taskId: raw.taskId ?? null };
  }
  return null;
}

/** Queue links (raw, from Rust). Returns how many were queued. */
export function enqueueContentLinks(raws) {
  let added = 0;
  for (const raw of Array.isArray(raws) ? raws : []) {
    const link = normalizeContentLink(raw);
    if (!link || queue.some((e) => sameLink(e.link, link))) continue;
    queue.push({ link, early: !ready });
    added += 1;
  }
  return added;
}

export function pendingContentLinks() {
  return queue.map((e) => e.link);
}

/** Open one link. Resolves true when it opened. */
export async function dispatchContentLink(link, { quietMiss = false } = {}) {
  if (link?.kind === 'document') {
    const { openDocumentFromLink } = await import('../components/documents-sidebar/openDocumentLink');
    return openDocumentFromLink(link, { showPanel: true, quietMiss });
  }
  if (link?.kind === 'board') {
    const { openBoardLink } = await import('../utils/boardLinks');
    return openBoardLink({ boardId: link.boardId, taskId: link.taskId, quietMiss });
  }
  return false;
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

async function openWithSettle(entry, sleep) {
  const attempts = entry.early ? SETTLE_ATTEMPTS : 1;
  for (let i = 1; i <= attempts; i += 1) {
    const last = i === attempts;
    // eslint-disable-next-line no-await-in-loop
    if (await dispatchContentLink(entry.link, { quietMiss: !last }).catch(() => false)) return true;
    // eslint-disable-next-line no-await-in-loop
    if (!last) await sleep(SETTLE_DELAY_MS);
  }
  return false;
}

/**
 * Open everything queued, in arrival order — only once the app is ready.
 * One drain at a time; links that arrive during it are picked up after.
 */
export async function drainContentLinks({ sleep = wait } = {}) {
  if (!ready) return 0;
  if (draining) return draining;
  draining = (async () => {
    let opened = 0;
    while (ready && queue.length) {
      const entry = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      if (await openWithSettle(entry, sleep)) opened += 1;
    }
    return opened;
  })();
  try {
    return await draining;
  } finally {
    draining = null;
  }
}

export function setContentLinksReady(value) {
  ready = Boolean(value);
}

/** Ask Rust for what arrived. Never throws (the e2e shim may not answer). */
export async function pullPendingContentLinks(invokeFn) {
  try {
    const invoke = invokeFn || (await import('@tauri-apps/api/core')).invoke;
    return enqueueContentLinks(await invoke(TAKE_COMMAND));
  } catch {
    return 0;
  }
}

export function resetContentLinksForTests() {
  queue = [];
  ready = false;
  draining = null;
}

/**
 * The page side. `ready` = signed in and past the desktop gate. Mounted in
 * App above the gate, so a link is received while the sign-in page is up and
 * opened the moment it goes away.
 */
export function useContentLinks({ ready: isReady }) {
  const readyRef = useRef(isReady);
  readyRef.current = isReady;

  useEffect(() => {
    setContentLinksReady(isReady);
    if (isReady) drainContentLinks();
  }, [isReady]);

  useEffect(() => {
    if (!isTauri()) return undefined;
    let disposed = false;
    let sub = null;
    const pull = async () => {
      await pullPendingContentLinks();
      if (!disposed && readyRef.current) drainContentLinks();
    };
    (async () => {
      const { subscribeTauriEvent } = await import('../utils/safeTauriEvent');
      if (disposed) return;
      sub = subscribeTauriEvent(PENDING_EVENT, () => { pull(); });
      await sub.ready;
      // A link that came before this listener (cold start): already in the inbox.
      if (!disposed) pull();
    })();
    return () => {
      disposed = true;
      if (sub) sub.unlisten();
    };
  }, []);
}
